import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import {
  BIBLE_VERSIONS,
  useBibleVersion,
} from "../../context/bibleVersionContext.js";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { detectBibleReference } from "../../utils/bibleReference";
import useVerseUrl, { BLOCKED_VERSE_URL } from "../../utils/useVerseUrl";
import { useVerseModal } from "../../context/verseModalContext";
import { findBestSearchMatch, getSearchSuggestions } from "../../utils/siteSearch";
import { isGroupOrPlace } from "../../utils/recordType";
import { pathsForSection } from "../../data/navigation/studyPaths";
import {
  useSearchCorpus,
  loadSearchCorpus,
  matchRecords,
} from "../../utils/searchCorpus";
import { decideSearchAction } from "../../utils/headerSearchCore";
import { Bookmark, CircleUserRound } from "lucide-react";
import { useMyStudy } from "../../utils/myStudy";
import { openAccount, useAccount } from "../../firebase/account";
import "./Header.css";

const Header = () => {
  const [expanded, setExpanded] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 992);

  const { bibleVersion, setBibleVersion } = useBibleVersion();

  /* Same builder the site's verse anchors use, and the modal they open. */
  const verseUrl = useVerseUrl();
  const { openVerseModal } = useVerseModal();


  /* The count rides on the bookmark so a reader who has saved something can
     see where it went; with nothing saved the button is just the way in. */
  const { items: savedStudyItems } = useMyStudy();
  const savedCount = savedStudyItems.length;

  const renderMyStudyLink = (extraClass = "") => (
    <Link
      to="/my-study"
      className={`ctb-helpTrigger ctb-myStudyTrigger ${extraClass}`}
      aria-label={
        savedCount ? `My Study, ${savedCount} saved` : "My Study"
      }
      title="My Study"
      onClick={() => setExpanded(false)}
    >
      <Bookmark size={16} aria-hidden="true" />
      {savedCount ? (
        <span className="ctb-myStudyTrigger-count" aria-hidden="true">
          {savedCount > 99 ? "99+" : savedCount}
        </span>
      ) : null}
    </Link>
  );
  /* Signed out, a person outline that opens sign-in; signed in, the reader's
     initial, which opens their account. Absent while accounts are off.
     Desktop only: a third round button beside the title on a phone clipped
     the site name, and there My Study's own account bar and the Save prompt
     are the ways in. */
  const account = useAccount();
  const accountInitial = (
    account.profile?.firstName ||
    account.user?.email ||
    ""
  )
    .trim()
    .charAt(0)
    .toUpperCase();

  const renderAccountButton = (extraClass = "") =>
    account.status === "off" ? null : (
      <button
        type="button"
        className={`ctb-helpTrigger ctb-accountTrigger ${
          account.status === "signedIn" ? "is-signedIn" : ""
        } ${extraClass}`}
        aria-label={
          account.status === "signedIn" ? "Your account" : "Sign in or create an account"
        }
        title={account.status === "signedIn" ? "Your account" : "Sign in"}
        onClick={() => {
          setExpanded(false);
          openAccount({ mode: "signIn" });
        }}
      >
        {account.status === "signedIn" && accountInitial ? (
          <span aria-hidden="true">{accountInitial}</span>
        ) : (
          <CircleUserRound size={17} aria-hidden="true" />
        )}
      </button>
    );

  const closeTimeoutRef = useRef(null);
  const subMenuCloseTimeoutRef = useRef(null);
  const searchAreaRef = useRef(null);
  const suggestionRefs = useRef([]);
  const searchInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const normalizeSearchText = (value) =>
    String(value || "")
      .toLowerCase()
      .trim()
      .replace(/[–—-]/g, " ")
      .replace(/[()]/g, "")
      .replace(/[.,:!?']/g, "")
      .replace(/\s+/g, " ");

  const closeMobileMenu = () => {
    setExpanded(false);
    setOpenMenu(null);
    setOpenSubMenu(null);
  };

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleCloseMenus = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
      setOpenSubMenu(null);
    }, 180);
  };

  const clearSubMenuCloseTimeout = () => {
    if (subMenuCloseTimeoutRef.current) {
      clearTimeout(subMenuCloseTimeoutRef.current);
      subMenuCloseTimeoutRef.current = null;
    }
  };

  const scheduleCloseSubMenu = () => {
    clearSubMenuCloseTimeout();

    subMenuCloseTimeoutRef.current = setTimeout(() => {
      setOpenSubMenu(null);
    }, 120);
  };

  const handleTopEnter = (menu) => {
    clearCloseTimeout();
    clearSubMenuCloseTimeout();

    setOpenMenu(menu);
    setOpenSubMenu(null);
  };

  const handleSubEnter = (submenu) => {
    clearCloseTimeout();
    clearSubMenuCloseTimeout();

    setOpenSubMenu(submenu);
  };

  /*
   * Desktop menus open on hover, which means `show` is controlled -- and a
   * controlled Dropdown stops running its own toggle logic. Every keyboard route
   * in (ArrowDown, which @restart/ui handles, and Enter, which the browser turns
   * into a click on the anchor) called an onToggle that was never wired up, so
   * the desktop nav was reachable by mouse only.
   *
   * Feeding onToggle back into the same state keeps hover and restores the
   * click, keyboard and click-outside handling react-bootstrap already
   * implements. Mobile deliberately leaves `show` undefined so the Dropdown
   * stays uncontrolled and keeps doing that for itself.
   *
   * headerKeyboard.cy.js covers both paths; header.cy.js does not, because
   * cy.click() fires mouseover first and so opens these through hover.
   */
  const topMenuProps = (menu) => ({
    show: isDesktop ? openMenu === menu : undefined,
    onToggle: (nextShow) => {
      if (!isDesktop) return;

      clearCloseTimeout();
      clearSubMenuCloseTimeout();

      setOpenMenu(nextShow ? menu : null);
      setOpenSubMenu(null);
    },
  });

  /* Leaves openMenu alone so the parent stays open under its own submenu. */
  const subMenuProps = (submenu) => ({
    show: isDesktop ? openSubMenu === submenu : undefined,
    onToggle: (nextShow) => {
      if (!isDesktop) return;

      clearCloseTimeout();
      clearSubMenuCloseTimeout();

      setOpenSubMenu(nextShow ? submenu : null);
    },
  });

  /*
   * Study paths, offered first in the menu they belong to.
   *
   * A nested submenu on purpose: one more item in the dropdown rather than a
   * restructure of it, reusing the submenu machinery and the keyboard handling
   * that headerKeyboard.cy.js already guards.
   *
   * Each link starts the path at its first step and carries ?path=, which is
   * what makes the on-page strip appear and what makes ContinueStory stand
   * down for the rest of the journey.
   */
  const renderStudyPaths = (sectionId) => {
    const paths = pathsForSection(sectionId);
    if (!paths.length) return null;

    return (
      <div
        className="ctb-nested-wrap"
        onMouseEnter={() => isDesktop && handleSubEnter(`paths-${sectionId}`)}
        onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
      >
        <NavDropdown
          title="Study Paths"
          id={`study-paths-${sectionId}-dropdown`}
          className="ctb-nested-dropdown ctb-studyPathsMenu"
          /* Right, matching every other nested submenu in the header. */
          drop={isDesktop ? "end" : "down"}
          {...subMenuProps(`paths-${sectionId}`)}
        >
          {paths.map((studyPath) => (
            <NavDropdown.Item
              key={studyPath.id}
              as={Link}
              to={`${studyPath.steps[0]}?path=${studyPath.id}`}
              onClick={closeMobileMenu}
            >
              {studyPath.title}
            </NavDropdown.Item>
          ))}
        </NavDropdown>
      </div>
    );
  };

  const bibleReference = useMemo(() => {
    return detectBibleReference(searchValue);
  }, [searchValue]);

  /*
   * Single source of truth for whether the suggestions panel exists, so the
   * input's ARIA cannot drift from what is actually rendered.
   */
  /*
   * People and battles arrive on demand. Importing them here put 617 kB into
   * the main bundle for every visitor, most of whom never search.
   */
  const { corpus, prime: primeSearchCorpus } = useSearchCorpus();

  const suggestionsOpen = Boolean(
    showSuggestions && (searchValue.trim() || bibleReference)
  );

  const peopleSuggestions = useMemo(() => {
    if (!corpus) return [];
    return corpus.getPeopleSuggestions(searchValue, 8);
  }, [corpus, searchValue]);

  const pageSuggestions = useMemo(() => {
    return getSearchSuggestions(searchValue, 4);
  }, [searchValue]);

  const eventSuggestions = useMemo(() => {
    const query = normalizeSearchText(searchValue);

    if (!query || !corpus) return [];

    return corpus.battles
      .map((battle) => {
        const title = normalizeSearchText(battle.title);
        const id = normalizeSearchText(battle.id);
        const book = normalizeSearchText(battle.book);
        const verse = normalizeSearchText(battle.verse);
        const location = normalizeSearchText(battle.location);
        const era = normalizeSearchText(battle.era);
        const scale = normalizeSearchText(battle.scale);
        const outcomeType = normalizeSearchText(battle.outcomeType);
        const outcome = normalizeSearchText(battle.outcome);
        const summary = normalizeSearchText(battle.summary);

        const participantText = normalizeSearchText(
          (battle.participants || [])
            .map((participant) =>
              [participant.personId, participant.role].filter(Boolean).join(" ")
            )
            .join(" ")
        );

        const nationText = normalizeSearchText((battle.nations || []).join(" "));

        let score = 0;

        if (title === query) score += 2200;
        else if (title.startsWith(query)) score += 1200;
        else if (title.includes(query)) score += 600;

        if (id === query) score += 2000;
        else if (id.startsWith(query)) score += 1000;
        else if (id.includes(query)) score += 500;

        if (book.includes(query)) score += 450;
        if (verse.includes(query)) score += 400;
        if (location.includes(query)) score += 350;
        if (era.includes(query)) score += 250;
        if (participantText.includes(query)) score += 250;
        if (nationText.includes(query)) score += 250;
        if (scale.includes(query)) score += 120;
        if (outcomeType.includes(query)) score += 120;
        if (summary.includes(query)) score += 100;
        if (outcome.includes(query)) score += 80;

        return {
          type: "event",
          title: battle.title,
          subtitle: [battle.book, battle.verse, battle.location]
            .filter(Boolean)
            .join(" • "),
          route: `/battles/${battle.id}`,
          score,
        };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
  }, [corpus, searchValue]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchAreaRef.current && !searchAreaRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscapeClose = (event) => {
      if (event.key === "Escape") {
        closeMobileMenu();
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };

    window.addEventListener("keydown", handleEscapeClose);
    return () => window.removeEventListener("keydown", handleEscapeClose);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setOpenSubMenu(null);
    setExpanded(false);
  }, [location.pathname]);

  /*
   * A searched reference opens the Scripture viewer, the same modal a verse
   * link anywhere else on the site opens.
   *
   * It used to window.open Bible Gateway in a new tab: the reader left the
   * site to read one verse and came back, if they came back, to a fresh page.
   * Every other verse on the site is a Bible Gateway anchor that the global
   * listener in verseModalContext intercepts -- but a window.open call is not
   * a click, so there is nothing to intercept, and this one had to be changed
   * rather than caught.
   *
   * The URL comes from the same builder the anchors use, so the selected Bible
   * version and the Deuterocanonical rules stay in one place. A blocked
   * reference yields the sentinel rather than a passage; leave the search
   * standing in that case, so the reader sees their query rather than an empty
   * box and no modal.
   */
  const openBibleReference = (reference) => {
    const href = verseUrl(reference);
    if (!href || href === BLOCKED_VERSE_URL) return;

    openVerseModal(href, reference);

    setSearchValue("");
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    closeMobileMenu();
  };

  const groupedSuggestions = useMemo(() => {
    const scripture = bibleReference
      ? [
          {
            type: "scripture",
            title: bibleReference,
            subtitle: "Open verse lookup",
            score: 10000,
          },
        ]
      : [];

    const allPeople = peopleSuggestions.map((item) => ({
      ...item,
      type: "person",
      score: Number(item.score || 0),
    }));

    /*
     * Nations, people groups and places live in the people index but are not
     * individuals, so they get their own section rather than sitting under a
     * "People" heading -- matching how person profiles present them.
     */
    const people = allPeople.filter((item) => !isGroupOrPlace(item.person));
    const groups = allPeople.filter((item) => isGroupOrPlace(item.person));

    const events = eventSuggestions.map((item) => ({
      ...item,
      type: "event",
      score: Number(item.score || 0),
    }));

    const pages = pageSuggestions
      .filter((item) => {
        const isDuplicatePerson = people.some(
          (person) => person.route === item.route || person.title === item.title
        );
        return !isDuplicatePerson;
      })
      .map((item) => ({
        ...item,
        type: "page",
        score: Number(item.score || 0),
      }));

    return {
      scripture,
      people,
      groups,
      events,
      pages,
    };
  }, [bibleReference, peopleSuggestions, eventSuggestions, pageSuggestions]);

  /*
   * Events, psalms, letters, eras and manuscripts: 786 records that were not
   * pages, so search had nowhere to send anyone. useDeepLink gave each one an
   * address, and the generated index carries the few fields needed to match.
   */
  const recordSuggestions = useMemo(() => {
    return matchRecords(corpus?.records, searchValue);
  }, [corpus, searchValue]);

  const recordsByGroup = useMemo(() => {
    return recordSuggestions.reduce((groups, record) => {
      (groups[record.group] = groups[record.group] || []).push(record);
      return groups;
    }, {});
  }, [recordSuggestions]);

  const hasSuggestions = useMemo(() => {
    return (
      groupedSuggestions.scripture.length > 0 ||
      groupedSuggestions.people.length > 0 ||
      groupedSuggestions.groups.length > 0 ||
      groupedSuggestions.events.length > 0 ||
      groupedSuggestions.pages.length > 0 ||
      recordSuggestions.length > 0
    );
  }, [groupedSuggestions, recordSuggestions]);

  const suggestionSections = useMemo(() => {
    return [
      {
        key: "scripture",
        label: "Scripture",
        icon: "📖",
        items: groupedSuggestions.scripture,
      },
      {
        key: "people",
        label: "People",
        icon: "👤",
        items: groupedSuggestions.people,
      },
      {
        key: "groups",
        label: "Nations & Groups",
        icon: "🛡",
        /* The 13 nations with no person record join the ones that have one. */
        items: [
          ...groupedSuggestions.groups,
          ...(recordsByGroup["Nations & Groups"] || []),
        ],
      },
      {
        key: "events",
        label: "Battles",
        icon: "⚔️",
        items: groupedSuggestions.events,
      },
      {
        key: "biblical-events",
        label: "Events",
        icon: "📅",
        items: recordsByGroup.Events || [],
      },
      {
        key: "psalms",
        label: "Psalms",
        icon: "🎵",
        items: recordsByGroup.Psalms || [],
      },
      {
        key: "letters",
        label: "Letters",
        icon: "✉️",
        items: recordsByGroup.Letters || [],
      },
      {
        key: "church-history",
        label: "Church history",
        icon: "⛪",
        items: recordsByGroup["Church history"] || [],
      },
      {
        key: "manuscripts",
        label: "Manuscripts",
        icon: "📜",
        items: recordsByGroup.Manuscripts || [],
      },
      {
        key: "pages",
        label: "Pages",
        icon: "🗺",
        items: groupedSuggestions.pages,
      },
    ].filter((section) => section.items.length > 0);
  }, [groupedSuggestions, recordsByGroup]);

  const flatSuggestions = useMemo(() => {
    return suggestionSections.flatMap((section) => section.items);
  }, [suggestionSections]);

  const topSuggestionIndex = useMemo(() => {
    if (!flatSuggestions.length) return -1;

    const query = normalizeSearchText(searchValue);

    const getPriorityScore = (item) => {
      const title = normalizeSearchText(item?.title);
      const subtitle = normalizeSearchText(item?.subtitle);
      const rawScore = Number(item?.score || 0);

      let priority = 0;

      if (item?.type === "scripture") {
        priority += 5000;
      }

      if (item?.type === "person") {
        priority += 2500;

        if (item?.matchedOn === "id-exact") priority += 5000;
        if (item?.matchedOn === "name-exact") priority += 4800;
        if (item?.matchedOn === "alias-exact") priority += 4400;

        if (item?.matchedOn === "id-prefix") priority += 2600;
        if (item?.matchedOn === "name-prefix") priority += 2400;
        if (item?.matchedOn === "alias-prefix") priority += 2200;

        if (item?.matchedOn === "id-partial") priority += 900;
        if (item?.matchedOn === "name-partial") priority += 800;
        if (item?.matchedOn === "alias-partial") priority += 700;

        if (item?.matchedOn === "title-partial") priority += 250;
        if (item?.matchedOn === "era-partial") priority += 150;
      }

      if (item?.type === "page") {
        priority += 1400;

        if (title === query) priority += 2200;
        else if (title.startsWith(query) && query) priority += 1000;
        else if (title.includes(query) && query) priority += 450;
      }

      // UPDATED EVENT PRIORITY LOGIC
      if (item?.type === "event") {
        priority += 350;

        if (title === query) priority += 700;
        else if (title.startsWith(query) && query) priority += 300;
        else if (title.includes(query) && query) priority += 120;
      }

      if (title === query) {
        priority += 400;
      } else if (title.startsWith(query) && query) {
        priority += 180;
      } else if (title.includes(query) && query) {
        priority += 80;
      }

      if (subtitle.includes(query) && query) {
        priority += 40;
      }

      return priority + rawScore;
    };

    let bestIndex = 0;
    let bestScore = getPriorityScore(flatSuggestions[0]);

    flatSuggestions.forEach((item, index) => {
      const currentScore = getPriorityScore(item);

      if (currentScore > bestScore) {
        bestScore = currentScore;
        bestIndex = index;
      }
    });

    return bestIndex;
  }, [flatSuggestions, searchValue]);

  useEffect(() => {
    suggestionRefs.current = [];
  }, [flatSuggestions]);

  useEffect(() => {
    if (!showSuggestions) {
      setActiveSuggestionIndex(-1);
      return;
    }

    if (!flatSuggestions.length) {
      setActiveSuggestionIndex(-1);
      return;
    }

    if (activeSuggestionIndex >= flatSuggestions.length) {
      setActiveSuggestionIndex(topSuggestionIndex >= 0 ? topSuggestionIndex : 0);
    }
  }, [showSuggestions, flatSuggestions, activeSuggestionIndex, topSuggestionIndex]);

  useEffect(() => {
    if (
      showSuggestions &&
      activeSuggestionIndex === -1 &&
      flatSuggestions.length > 0 &&
      searchValue.trim()
    ) {
      setActiveSuggestionIndex(topSuggestionIndex >= 0 ? topSuggestionIndex : 0);
    }
  }, [
    showSuggestions,
    activeSuggestionIndex,
    flatSuggestions,
    topSuggestionIndex,
    searchValue,
  ]);

  useEffect(() => {
    if (activeSuggestionIndex >= 0 && suggestionRefs.current[activeSuggestionIndex]) {
      suggestionRefs.current[activeSuggestionIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeSuggestionIndex]);

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === "scripture") {
      openBibleReference(suggestion.title);
      return;
    }

    if (suggestion.route) {
      navigate(suggestion.route);
      setSearchValue("");
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      closeMobileMenu();
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    /*
     * Someone can type and hit enter faster than the corpus arrives. Waiting
     * here costs a moment on that first search; not waiting would quietly
     * answer a person's name with a page result, or nothing at all.
     */
    const ready = corpus || (await loadSearchCorpus());

    const action = decideSearchAction({
      value: searchValue,
      bibleReference,
      matchedPerson: ready ? ready.findPersonMatch(searchValue) : null,
      matchedPage: findBestSearchMatch(searchValue),
      showSuggestions,
      activeSuggestionIndex,
      flatSuggestions,
    });

    const scoredPersonMatch = ready
      ? ready.findPersonMatchWithScore(searchValue)
      : null;
    const bestPageMatch = findBestSearchMatch(searchValue);

    if (!action) return;

    if (action.type === "open-scripture") {
      openBibleReference(action.value);
      return;
    }

    if (action.type === "navigate") {
      const isSearchPage = action.value.startsWith("/search?");

      if (isSearchPage) {
        navigate(action.value, {
          state: {
            possibilities: {
              scripture: bibleReference ? [bibleReference] : [],
              people: scoredPersonMatch,
              pages: bestPageMatch,
            },
          },
        });
      } else {
        navigate(action.value);
      }

      setSearchValue("");
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      closeMobileMenu();
    }
  };

  const handleSearchKeyDown = (e) => {
    const canNavigateSuggestions =
      showSuggestions && (flatSuggestions.length > 0 || searchValue.trim() || bibleReference);

    if (!canNavigateSuggestions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (!flatSuggestions.length) return;

      setShowSuggestions(true);
      setActiveSuggestionIndex((prev) => {
        if (prev < 0) return topSuggestionIndex >= 0 ? topSuggestionIndex : 0;
        return prev === flatSuggestions.length - 1 ? 0 : prev + 1;
      });
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      if (!flatSuggestions.length) return;

      setShowSuggestions(true);
      setActiveSuggestionIndex((prev) => {
        if (prev < 0) return topSuggestionIndex >= 0 ? topSuggestionIndex : flatSuggestions.length - 1;
        return prev === 0 ? flatSuggestions.length - 1 : prev - 1;
      });
    }

    if (e.key === "Enter") {
      const selectedIndex =
        activeSuggestionIndex >= 0 ? activeSuggestionIndex : topSuggestionIndex;

      if (selectedIndex >= 0 && flatSuggestions[selectedIndex]) {
        e.preventDefault();
        handleSuggestionClick(flatSuggestions[selectedIndex]);
        return;
      }
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const renderSuggestionSection = (section, startIndex) => {
    if (!section.items.length) return null;

    return (
      <div className="ctb-search-group" key={section.key}>
        <div className="ctb-search-group-label">
          <span className="ctb-search-group-icon">{section.icon}</span>
          <span>{section.label}</span>
        </div>

        <div className="ctb-search-group-items">
          {section.items.map((suggestion, index) => {
            const globalIndex = startIndex + index;
            const isActive = globalIndex === activeSuggestionIndex;
            const isTopMatch = globalIndex === topSuggestionIndex;

            return (
              <button
                key={`${section.label}-${suggestion.route || suggestion.title}-${index}`}
                type="button"
                className={`ctb-search-suggestion ${isActive ? "is-active" : ""} ${
                  isTopMatch ? "is-top-match" : ""
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setActiveSuggestionIndex(globalIndex)}
                ref={(el) => {
                  suggestionRefs.current[globalIndex] = el;
                }}
              >
                <div className="ctb-search-suggestion-main">
                  <span className="ctb-search-suggestion-title">{suggestion.title}</span>

                  {isTopMatch ? (
                    <span className="ctb-search-top-badge">Top match</span>
                  ) : null}
                </div>

                {suggestion.subtitle ? (
                  <span className="ctb-search-suggestion-subtitle">
                    {suggestion.subtitle}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* `compact` shows the bare abbreviation, for the mobile row where the full
     name only ever displayed as a clipped "NIV — Ne". */
  const renderBibleVersionSelect = (extraClass = "", compact = false) => (
    <label className={`header-bibleVersionWrap ${extraClass}`}>
      <select
        className="header-bibleVersionSelect"
        value={bibleVersion}
        onChange={(e) => setBibleVersion(e.target.value)}
        aria-label="Select Bible version"
      >
        {BIBLE_VERSIONS.map((version) => (
          <option key={version.id} value={version.id}>
            {compact ? version.id : `${version.id} — ${version.name}`}
          </option>
        ))}
      </select>
    </label>
  );

  const renderSearch = (extraClass = "") => (
    <div className={`ctb-searchBlock ${extraClass}`}>
      <form className="ctb-search" onSubmit={handleSearchSubmit}>
        <div className="ctb-search-wrapper">
          <input
            ref={searchInputRef}
            type="text"
            className="ctb-search-input"
            placeholder={
              isDesktop
                ? "🔍 Search Scripture, people, books, battles..."
                : "🔍 Search"
            }
            value={searchValue}
            onChange={(e) => {
              primeSearchCorpus();
              setSearchValue(e.target.value);
              setShowSuggestions(true);
              setActiveSuggestionIndex(-1);
            }}
            onFocus={() => {
              setShowSuggestions(true);
              primeSearchCorpus();
            }}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
            aria-autocomplete="list"
            /*
             * The suggestions panel only renders while it has something to
             * show, so naming it unconditionally left aria-controls pointing at
             * an element that was usually absent from the document -- a
             * critical violation on every route.
             *
             * No aria-expanded here: this input has the implicit textbox role,
             * which does not support it. Announcing the open state properly
             * means promoting the input to role="combobox" and the panel to a
             * listbox of options, which is worth doing deliberately rather
             * than half-applying.
             */
            aria-controls={suggestionsOpen ? "ctb-search-suggestions" : undefined}
          />

          {searchValue ? (
            <button
              type="button"
              className="ctb-search-clear"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>
      </form>

      {suggestionsOpen && (
        <div className="ctb-search-suggestions" id="ctb-search-suggestions">
          <div className="ctb-search-suggestions-head">Search Results</div>

          {suggestionSections.map((section, sectionIndex) => {
            const startIndex = suggestionSections
              .slice(0, sectionIndex)
              .reduce((sum, current) => sum + current.items.length, 0);

            return renderSuggestionSection(section, startIndex);
          })}

          {!hasSuggestions && searchValue.trim() && (
            <div className="ctb-search-empty">
              No direct match found. Press Enter for search results.
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Navbar
      expand="lg"
      className="ctb-navbar"
      expanded={expanded}
      onToggle={(nextExpanded) => setExpanded(nextExpanded)}
    >
      <Container className="ctb-navbar-container" ref={searchAreaRef}>
        <div className="ctb-navbar-topRow">
          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            className="ctb-navbar-toggle"
          />

          <Nav.Link
            as={Link}
            to="/"
            className="header-title ctb-mobile-title"
            onClick={closeMobileMenu}
          >
            <span className="header-title-text">Compass Through The Bible</span>
          </Nav.Link>

          {/* My Study holds the top-right corner on phones. The site guide
              button that sat here is desktop-only now: two round buttons
              beside the title squeezed it, and the guide repeats what each
              page's own Study Guide says. */}
          {renderMyStudyLink("ctb-myStudyTrigger--mobile")}
        </div>

        {!isDesktop && !expanded && (
          <div className="ctb-mobile-actions">
            {/* One row under the title: search and version. The version
                picker used to sit on a row of its own, which made the header
                three rows tall. */}
            <div className="ctb-mobile-searchRow">
              <div className="ctb-mobile-searchWrap">
                {renderSearch("ctb-searchBlock--mobile")}
              </div>

              <div className="ctb-mobile-versionWrap">
                {renderBibleVersionSelect("header-bibleVersionWrap--mobile", true)}
              </div>
            </div>
          </div>
        )}

        {expanded && !isDesktop && (
          <div
            className="ctb-mobile-backdrop"
            onClick={closeMobileMenu}
            role="presentation"
          />
        )}

        <Navbar.Collapse id="basic-navbar-nav" className="ctb-mobile-drawer">
          {/* {!isDesktop && (
            <div className="ctb-mobile-drawer-header">
              <span className="ctb-mobile-drawer-title">Menu</span>

              <button
                type="button"
                className="ctb-mobile-close"
                onClick={closeMobileMenu}
                aria-label="Close navigation menu"
              >
                ×
              </button>
            </div>
          )} */}

          <div className="ctb-navbar-inner">
            <Nav className="ctb-nav">
              <Nav.Link
                as={Link}
                to="/"
                className="header-title ctb-desktop-title"
                onClick={closeMobileMenu}
              >
                <span className="header-title-text">Compass Through The Bible</span>
              </Nav.Link>

              <div
                className="ctb-dropdown-wrap"
                onMouseEnter={() => isDesktop && handleTopEnter("old-testament")}
                onMouseLeave={() => isDesktop && scheduleCloseMenus()}
              >
                <NavDropdown
                  title="Old Testament"
                  id="old-test-dropdown"
                  className="nav-title ctb-main-dropdown"
                  {...topMenuProps("old-testament")}
                >
                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("torah")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Torah"
                      id="torah-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("torah")}
                    >
                      <NavDropdown.Item as={Link} to="/genesis" onClick={closeMobileMenu}>
                        Journey Through Genesis
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/exodus" onClick={closeMobileMenu}>
                        Journey Through Exodus
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/leviticus" onClick={closeMobileMenu}>
                        Journey Through Leviticus
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/numbers" onClick={closeMobileMenu}>
                        Journey Through Numbers
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/exodus-deuteronomy"
                        onClick={closeMobileMenu}
                      >
                        Journey Through The Wilderness
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("histories")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Histories"
                      id="histories-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("histories")}
                    >
                      <NavDropdown.Item as={Link} to="/joshua" onClick={closeMobileMenu}>
                        Conquest of Canaan
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/judges" onClick={closeMobileMenu}>
                        Period of the Judges
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/1samuel-2chron"
                        onClick={closeMobileMenu}
                      >
                        United to Divided Kingdoms
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/ezra-nehemiah-esther" onClick={closeMobileMenu}>
                        Post Exile Explorer
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("wisdom")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Wisdoms"
                      id="wisdom-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("wisdom")}
                    >
                      <NavDropdown.Item as={Link} to="/job" onClick={closeMobileMenu}>
                        Job
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/psalms" onClick={closeMobileMenu}>
                        Psalms
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/proverbs" onClick={closeMobileMenu}>
                        Proverbs
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/ecclesiastes" onClick={closeMobileMenu}>
                        Ecclesiastes
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/song-of-songs" onClick={closeMobileMenu}>
                        Song of Songs
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() =>
                      isDesktop && handleSubEnter("major-prophets")
                    }
                    onMouseLeave={() =>
                      isDesktop && scheduleCloseSubMenu()
                    }
                  >
                    <NavDropdown
                      title="Major Prophets"
                      id="major-prophets-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      show={
                        isDesktop
                          ? openSubMenu === "major-prophets"
                          : undefined
                      }
                    >
                      <NavDropdown.Item
                        as={Link}
                        to="/isaiah"
                        onClick={closeMobileMenu}
                      >
                        Isaiah
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/jeremiah"
                        onClick={closeMobileMenu}
                      >
                        Jeremiah
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/ezekiel"
                        onClick={closeMobileMenu}
                      >
                        Ezekiel
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/daniel"
                        onClick={closeMobileMenu}
                      >
                        Daniel
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <NavDropdown.Item
                    as={Link}
                    to="/minor-prophets"
                    onMouseEnter={() => {
                      if (!isDesktop) return;

                      clearSubMenuCloseTimeout();
                      setOpenSubMenu(null);
                    }}
                    onClick={closeMobileMenu}
                  >
                    Minor Prophets
                  </NavDropdown.Item>

                  <NavDropdown.Divider />
                  {renderStudyPaths("old-testament")}
                </NavDropdown>
              </div>

              <div
                className="ctb-dropdown-wrap"
                onMouseEnter={() => isDesktop && handleTopEnter("new-testament")}
                onMouseLeave={() => isDesktop && scheduleCloseMenus()}
              >
                <NavDropdown
                  title="New Testament"
                  id="new-testament-dropdown"
                  className="nav-title ctb-main-dropdown"
                  {...topMenuProps("new-testament")}
                >
                  <NavDropdown.Item as={Link} to="/walk-through-the-gospels" onClick={closeMobileMenu}>
                    A Walk Through the Gospels
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/church-history" onClick={closeMobileMenu}>
                    Church History through Acts
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/church-letters" onClick={closeMobileMenu}>
                    Church Letters
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/revelation" onClick={closeMobileMenu}>
                    Revelation
                  </NavDropdown.Item>

                  <NavDropdown.Divider />
                  {renderStudyPaths("new-testament")}
                </NavDropdown>
              </div>

              {isDesktop ? (
                <div
                  className="ctb-dropdown-wrap"
                  onMouseEnter={() => isDesktop && handleTopEnter("events")}
                  onMouseLeave={() => isDesktop && scheduleCloseMenus()}
                >
                  <NavDropdown
                    title="Events"
                    id="events-dropdown"
                    className="nav-title ctb-main-dropdown"
                    {...topMenuProps("events")}
                  >
                    <NavDropdown.Item as={Link} to="/events" onClick={closeMobileMenu}>
                      Biblical Events Explorer
                    </NavDropdown.Item>
                  </NavDropdown>
                </div>
              ) : (
                <Nav.Link
                  as={Link}
                  to="/events"
                  className="ctb-mobile-directLink"
                  onClick={closeMobileMenu}
                >
                  Biblical Events Explorer
                </Nav.Link>
              )}

              <div
                className="ctb-dropdown-wrap"
                onMouseEnter={() => isDesktop && handleTopEnter("genealogies")}
                onMouseLeave={() => isDesktop && scheduleCloseMenus()}
              >
                <NavDropdown
                  title="People of the Bible"
                  id="genealogies-top-dropdown"
                  className="nav-title ctb-main-dropdown"
                  {...topMenuProps("genealogies")}
                >
                  <NavDropdown.Item as={Link} to="/adam-noah" onClick={closeMobileMenu}>
                    Adam to Noah
                  </NavDropdown.Item>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("noah")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Noah’s Sons"
                      id="noah-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("noah")}
                    >
                      <NavDropdown.Item as={Link} to="/japheth-lineage" onClick={closeMobileMenu}>
                        Japheth
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/ham-lineage" onClick={closeMobileMenu}>
                        Ham
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/shem-lineage" onClick={closeMobileMenu}>
                        Shem
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("terah")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Terah’s House"
                      id="terah-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("terah")}
                    >
                      <NavDropdown.Item as={Link} to="/abraham-lineage" onClick={closeMobileMenu}>
                        Abraham
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/nahor-lineage" onClick={closeMobileMenu}>
                        Nahor
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/haran-lineage" onClick={closeMobileMenu}>
                        Haran
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("abraham-line")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Abraham’s Line"
                      id="abraham-line-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("abraham-line")}
                    >
                      <NavDropdown.Item as={Link} to="/jacob-lineage" onClick={closeMobileMenu}>
                        Jacob
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/esau-lineage" onClick={closeMobileMenu}>
                        Esau
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("levi-line")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Levi Line / Priests"
                      id="levi-line-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("levi-line")}
                    >
                      <NavDropdown.Item as={Link} to="/moses-aaron" onClick={closeMobileMenu}>
                        Moses & Aaron
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/levi-line" onClick={closeMobileMenu}>
                        Levi Line
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/second-temple-leaders" onClick={closeMobileMenu}>
                        Second Temple Leaders
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("kings")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Kings"
                      id="kings-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("kings")}
                    >
                      <NavDropdown.Item as={Link} to="/saul-lineage" onClick={closeMobileMenu}>
                        Saul
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/david-lineage" onClick={closeMobileMenu}>
                        David
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/kings-of-judah" onClick={closeMobileMenu}>
                        Kings of Judah
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <hr />

                  <NavDropdown.Item as={Link} to="/enemies-of-the-bible" onClick={closeMobileMenu}>
                    Enemies of the Bible
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/nations-of-the-bible" onClick={closeMobileMenu}>
                    Nations of the Bible
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/women-of-the-bible" onClick={closeMobileMenu}>
                    Women of the Bible
                  </NavDropdown.Item>
                </NavDropdown>
              </div>

              <div
                className="ctb-dropdown-wrap"
                onMouseEnter={() => isDesktop && handleTopEnter("history-text")}
                onMouseLeave={() => isDesktop && scheduleCloseMenus()}
              >
                <NavDropdown
                  title="History & Context"
                  id="history-text-dropdown"
                  className="nav-title ctb-main-dropdown"
                  {...topMenuProps("history-text")}
                >
                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("history")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="History"
                      id="history-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("history")}
                    >
                      <NavDropdown.Item
                        as={Link}
                        to="/who-ruled-the-biblical-land"
                        onClick={closeMobileMenu}
                      >
                        Who Ruled the Biblical Land?
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/david-alexander" onClick={closeMobileMenu}>
                        David to Alexander
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/alexander-earlychurch"
                        onClick={closeMobileMenu}
                      >
                        Alexander to the Early Church
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/between-the-testaments"
                        onClick={closeMobileMenu}
                      >
                        Between the Testaments
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/early-church-history" onClick={closeMobileMenu}>
                        Early Church History
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/early-church-fathers" onClick={closeMobileMenu}>
                        Early Church Fathers
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <div
                    className="ctb-nested-wrap"
                    onMouseEnter={() => isDesktop && handleSubEnter("scripture-preservation")}
                    onMouseLeave={() => isDesktop && scheduleCloseSubMenu()}
                  >
                    <NavDropdown
                      title="Scripture Preservation"
                      id="scripture-preservation-dropdown"
                      drop={isDesktop ? "end" : "down"}
                      className="ctb-nested-dropdown"
                      {...subMenuProps("scripture-preservation")}
                    >
                      <NavDropdown.Item
                        as={Link}
                        to="/scripture-preservation"
                        onClick={closeMobileMenu}
                      >
                        Preservation of Scripture
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/scripture-formation-recognition"
                        onClick={closeMobileMenu}
                      >
                        Scripture Formation & Recognition
                      </NavDropdown.Item>
                    </NavDropdown>
                  </div>

                  <NavDropdown.Divider />
                  {renderStudyPaths("history")}
                </NavDropdown>
              </div>
            </Nav>

            {isDesktop && (
              <div className="ctb-desktop-actions">
                <div className="ctb-desktop-versionWrap">
                  {renderBibleVersionSelect("header-bibleVersionWrap--desktop")}
                </div>

                <div className="ctb-desktop-searchWrap">
                  {renderSearch("ctb-searchBlock--desktop")}
                </div>

                {/* Inside the actions group so they share the search
                    box's room at laptop widths. */}
                {renderMyStudyLink("ctb-helpTrigger--desktop")}
                {renderAccountButton("ctb-helpTrigger--desktop")}
              </div>
            )}
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;